import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil, startWith } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from '../../core/theme/theme.service';
import {
  BrandingService,
  LocalizedStudentBranding,
  localizeStudentBranding,
  emptyStudentPortalBranding,
} from '../../core/branding/branding.service';
import { LanguageService } from '../../services/language.service';

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FooterComponent implements OnInit, OnDestroy {
  currentYear = new Date().getFullYear();
  siteLogoSrc = this.themeService.getSiteLogoUrl();
  branding: LocalizedStudentBranding = localizeStudentBranding(
    emptyStudentPortalBranding(),
    'en'
  );
  private readonly destroy$ = new Subject<void>();

  constructor(
    private themeService: ThemeService,
    private brandingService: BrandingService,
    private translate: TranslateService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.themeService.siteLogoUrl$
      .pipe(takeUntil(this.destroy$))
      .subscribe((url) => {
        this.siteLogoSrc = url;
      });

    const lang = this.languageService.getCurrentLanguage();
    combineLatest([
      this.brandingService.branding$,
      this.translate.onLangChange.pipe(startWith({ lang })),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([raw, langEvent]) => {
        this.branding = localizeStudentBranding(raw, langEvent.lang || lang);
      });
  }

  get logoAlt(): string {
    const title = this.branding.programTitle?.trim();
    const subtitle = this.branding.programSubtitle?.trim();
    if (title && subtitle) {
      return `${title} ${subtitle}`;
    }
    return title || subtitle || 'Logo';
  }

  get phoneHref(): string {
    const digits = (this.branding.contactPhone || '').replace(/[^\d+]/g, '');
    return digits ? `tel:${digits}` : '';
  }

  get addressLines(): string[] {
    return (this.branding.contactAddress || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
